import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, themeVars, Text, AlertInline, ExclamationCircleIcon } from '@eds-web/react';
import styled from '@emotion/styled';

import { Prize } from './Prize';
import { PriceSkeleton } from '../common/Skeleton';
import { StyledColumn } from './Prizes.style';
import { Winning } from '../../types/prize.types';
import { usePrizeListData } from '../../hooks/usePrizeListData';
import { SORT_TYPE } from '../../constants/sort';
import usePrizeSort from '../../hooks/usePrizeSort';
import { usePrizeExpansion } from '../../contexts/PrizeExpansionContext';
import { device } from '../../constants/breakpoint';

const StyledStack = styled(Stack)`
    & label {
        font-style: normal;
        color: ${themeVars.color.transparent.light['100']};
        font-family: ${themeVars.font.family.display};
    }
    & select {
        background: transparent;
        outline: 0;
        border: solid 0;
        color: ${themeVars.color.transparent.light['100']};
    }
    & option {
        color: ${themeVars.color.transparent.dark['100']};
    }
`;

interface SortOption {
    value: string;
    label: string;
}

interface PrizeSelectionProps {
    onSortChange: (sortType: SORT_TYPE) => void;
    currentSortType: SORT_TYPE;
}

const PrizeSelection: React.FC<PrizeSelectionProps> = ({ onSortChange, currentSortType }) => {
    const { t } = useTranslation();

    const sortOptions: SortOption[] = useMemo(
        () => [
            { value: SORT_TYPE.MOST_RECENT, label: t('epic.esport.common.most_recent') },
            { value: SORT_TYPE.GAME_TITLE, label: t('epic.esport.common.game_title') },
            { value: SORT_TYPE.UNPAID_TO_PAID, label: t('epic.esport.common.unpaid_to_paid') },
            { value: SORT_TYPE.PAID_TO_UNPAID, label: t('epic.esport.common.paid_to_unpaid') }
        ],
        [t]
    );

    const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedSortType = event.target.value as SORT_TYPE;
        onSortChange(selectedSortType);
    };

    return (
        <StyledStack direction="row">
            <label htmlFor="sortBy">
                <Text
                    style={{ color: themeVars.color.transparent.light['065'], textAlign: 'center' }}
                    variant={'paragraphSM'}
                >
                    {t('epic.esport.common.sort_by')}
                </Text>
            </label>
            <select name="sortBy" id="sortBy" value={currentSortType} onChange={handleSortChange}>
                {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                        <Text
                            style={{
                                color: themeVars.color.transparent.light['100'],
                                textAlign: 'center'
                            }}
                            variant={'paragraphMD'}
                        >
                            {option.label}
                        </Text>
                    </option>
                ))}
            </select>
        </StyledStack>
    );
};

const HeaderContainer = styled.div`
    @media screen and ${device.laptop} {
        display: none;
    }
`;

const MobileHeaderContainer = styled.div`
    display: none;
    @media screen and ${device.laptop} {
        display: block;
        padding-left: 24px;
    }
`;

const ListHeader: React.FC<{ hideRow: boolean }> = ({ hideRow }) => {
    const { t } = useTranslation();

    const headerLabels = useMemo(
        () => [
            t('epic.esport.prizes.list.header.game'),
            t('epic.esport.prizes.list.header.event'),
            t('epic.esport.prizes.list.header.date'),
            t('epic.esport.prizes.list.header.type'),
            t('epic.esport.prizes.list.header.price'),
            t('epic.esport.prizes.list.header.tax'),
            t('epic.esport.prizes.list.header.payment')
        ],
        [t]
    );

    if (hideRow) {
        return null;
    }

    return (
        <>
            <HeaderContainer>
                <div style={{ display: 'flex', padding: '0 48px 0 24px' }}>
                    {headerLabels.map((label, index) => (
                        <StyledColumn
                            key={index}
                            variant="uiMD"
                            $isEvent={label === t('epic.esport.prizes.list.header.event')}
                        >
                            {label}
                        </StyledColumn>
                    ))}
                    <div style={{ width: '55px' }} />
                </div>
            </HeaderContainer>
            <MobileHeaderContainer>
                <Text
                    variant={'paragraphMD'}
                    style={{ color: themeVars.color.transparent.light['065'] }}
                >
                    {t('epic.esport.prizes.list.header.game')}
                </Text>
            </MobileHeaderContainer>
        </>
    );
};

interface ListProps {
    sortedPrizes: Winning[];
    identityTaxVerified: boolean;
    offlinePayment: boolean;
    loading: boolean;
    error: string | null;
}

const List: React.FC<ListProps> = ({ sortedPrizes, loading, error }) => {
    const { t } = useTranslation();

    if (loading) {
        return <PriceSkeleton />;
    }

    if (error) {
        return (
            <AlertInline status={'critical'} message={t('errors.esport.prizes.generic.error')} />
        );
    }

    if (!sortedPrizes?.length) {
        return (
            <Stack
                justify={'center'}
                align={'center'}
                style={{
                    backgroundColor: themeVars.color.background.elevated.low,
                    height: 200,
                    borderRadius: 24
                }}
            >
                <Text
                    variant={'paragraphMD'}
                    style={{ color: themeVars.color.transparent.light['100'] }}
                >
                    <ExclamationCircleIcon size={28} style={{ marginRight: 8 }} />
                </Text>
                <Text
                    variant={'paragraphMD'}
                    style={{ color: themeVars.color.transparent.light['065'] }}
                >
                    {t('epic.esport.prizes.empty')}
                </Text>
            </Stack>
        );
    }

    return (
        <>
            {sortedPrizes.map((winning: Winning, index: number) => (
                <Prize key={winning.prizeId || index} winning={winning} />
            ))}
        </>
    );
};

export const PrizeList: React.FC = () => {
    const { prizes, identityTaxVerified, loading, error, offlinePayment } = usePrizeListData();
    const { sortedPrizes, currentSortType, setSortType } = usePrizeSort(prizes || []);
    const { setSortedPrizes } = usePrizeExpansion();

    React.useEffect(() => {
        if (sortedPrizes.length > 0) {
            setSortedPrizes(sortedPrizes);
        }
    }, [sortedPrizes, setSortedPrizes]);

    return (
        <>
            <PrizeSelection onSortChange={setSortType} currentSortType={currentSortType} />
            <ListHeader hideRow={!sortedPrizes?.length} />
            <List
                sortedPrizes={sortedPrizes}
                identityTaxVerified={identityTaxVerified}
                offlinePayment={offlinePayment}
                loading={loading}
                error={error}
            />
        </>
    );
};
